import type { GetStaticProps, GetStaticPropsContext } from 'next'
import { ChangeEvent, Fragment, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'

import ExamEntry from '~/components/Exams/ExamEntry'
import Header from '~/components/Header'
import { COMMON_TRANSLATION_MODULE_NAMES, DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES } from '~/shared/layouts/constants'
import { DefaultLayout } from '~/shared/layouts/DefaultLayout'
import { Select } from '~/components/Select'
import { isAuthenticated } from '~/auth/slices/auth.slice'
import { useAppSelector } from '~/redux/hooks'
import { usePageTitle } from '~/utils/tags'
import Metatags from '~/components/Metadata/Metatags'
import norwayExams from '~/shared/constants/norwayExams.const'

const gradeLevels = ['8-10. klasse', '1P', '2P', '1T', 'S1', 'S2', 'R1', 'R2']

export default function Exams(): React.ReactElement {
  const { t } = useTranslation('exams')
  const [gradeLevel, setGradeLevel] = useState('')

  const authenticated = useAppSelector(isAuthenticated)

  const filtered = norwayExams?.filter((v) => v.classroom === gradeLevel.slice(0, 4))
  const reversed = filtered?.reverse() // eslint-disable-line fp/no-mutating-methods

  return (
    <div>
      <Metatags metaDescription={t('metaDescription')} otherLocales={[]} title={usePageTitle(t('titleTag'))} />

      <DefaultLayout zeroPadding>
        <div className="min-h-screen py-8 px-4 lg:px-8 sm:px-16">
          <section className="max-w-3xl mx-auto">
            <Header isTextWhite>{t('title')}</Header>
            <Select
              className="!m-0 self-start bg-purple hover:bg-purple-dark text-white"
              iconColor="white"
              onChange={(evt: ChangeEvent<HTMLSelectElement>) => setGradeLevel(evt.target.value)}
              value={gradeLevel}
            >
              <option className="text-black" value="">
                Velg klassetrinn
              </option>
              {gradeLevels.map((option) => (
                <option className="text-black" key={option}>
                  {option}
                </option>
              ))}
            </Select>
            <div className="lg:max-w-xl mt-8 pb-12">
              <div className="py-6 px-6 rounded shadow-experimental bg-white">
                <p>{t('description')}</p>
              </div>
              {reversed && reversed.length > 0 && (
                <div className="mt-6 px-4 py-6 md:p-12 rounded shadow-experimental bg-white">
                  {reversed?.map((exam, index) => (
                    <Fragment key={exam.term + exam.problemUrl}>
                      <ExamEntry
                        classroom={exam.classroom}
                        isLoggedIn={authenticated}
                        problemUrl={exam.problemUrl}
                        solutionUrl={exam.solutionUrl}
                        term={termTitle(exam.term)}
                      />
                      {index !== reversed.length - 1 && <hr className="w-full h-px my-8 border-purple-light" />}
                    </Fragment>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
      </DefaultLayout>
    </div>
  )
}

function termTitle(term: string): string {
  if (term.startsWith('V')) {
    return `Våren ${term.slice(1)}`
  } else {
    return `Høsten ${term.slice(1)}`
  }
}

export const getStaticProps: GetStaticProps = async ({ locale }: GetStaticPropsContext) => {
  if (locale === 'en') {
    return {
      notFound: true,
    }
  }
  return {
    props: {
      ...(await serverSideTranslations(locale as string, [
        ...DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES,
        ...COMMON_TRANSLATION_MODULE_NAMES,
        'exams',
      ])),
    },
  }
}
